import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SUPPORTED_LANGUAGES, type AppLanguage, activeLanguage } from '@/i18n';

const LABELS: Record<AppLanguage, string> = { es: 'Español', en: 'English' };
const SHORT: Record<AppLanguage, string> = { es: 'ES', en: 'EN' };

function GlobeIcon(): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  );
}

/**
 * Accessible ES/EN language toggle for Commerce. Persists the choice (i18next
 * localStorage detector) and switches the UI at runtime without a reload.
 */
export function LanguageSwitcher(): JSX.Element {
  const { t, i18n } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const current = activeLanguage();

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('language.label')}
        data-testid="language-switcher"
        className="inline-flex items-center gap-1.5 rounded-card px-3 py-1.5 text-sm text-neutral-600 transition-colors hover:bg-neutral-50 hover:text-neutral-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fc-gold"
      >
        <GlobeIcon />
        <span>{SHORT[current]}</span>
      </button>
      {open && (
        <ul
          role="listbox"
          aria-label={t('language.label')}
          className="absolute right-0 top-full z-30 mt-1 min-w-[160px] rounded-card border border-fc-line bg-fc-paper py-1 shadow-lg"
        >
          {SUPPORTED_LANGUAGES.map((lang) => {
            const selected = lang === current;
            return (
              <li key={lang} role="option" aria-selected={selected}>
                <button
                  type="button"
                  onClick={() => {
                    void i18n.changeLanguage(lang);
                    setOpen(false);
                  }}
                  data-testid={`language-option-${lang}`}
                  className={`block w-full px-4 py-2 text-left text-sm transition-colors ${
                    selected
                      ? 'bg-neutral-100 font-medium text-neutral-900'
                      : 'text-neutral-700 hover:bg-neutral-50 hover:text-neutral-900'
                  }`}
                >
                  {LABELS[lang]}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
